import { set } from "lodash-es";
import type { IntlFormatters } from "react-intl";

import type { TranslationID } from "@citrine/lang/utils";

import { trimExcessSignificance } from "./rounding";

export function wrapFormikErrors(errors: any): any {
  if (Array.isArray(errors)) {
    const wrappedErrors = errors.map(wrapFormikErrors);

    return wrappedErrors.some(Boolean) ? wrappedErrors : undefined;
  }

  if (typeof errors === "object") {
    const wrappedErrors = Object.entries(errors)
      .map(([key, value]) => [key, wrapFormikErrors(value)])
      .filter(([, value]) => value);

    return wrappedErrors.length ? Object.fromEntries(wrappedErrors) : undefined;
  }

  return errors;
}

export const getCeiling = (...bounds: number[]) =>
  Math.min(...bounds.filter((bound) => !isNaN(bound)));

export const getFloor = (...bounds: number[]) =>
  Math.max(...bounds.filter((bound) => !isNaN(bound)));

/**
 * validation helpers
 *
 * Use inverted logic to allow NaN to pass
 */
export const isGreaterThan = (...bounds: number[]) => {
  const floor = getFloor(...bounds);
  return (val: number = NaN) => !(val <= floor);
};

export const isGreaterThanOrEqualTo = (...bounds: number[]) => {
  const floor = getFloor(...bounds);
  return (val: number = NaN) => !(val < floor);
};

export const isLessThan = (...bounds: number[]) => {
  const ceiling = getCeiling(...bounds);
  return (val: number = NaN) => !(val >= ceiling);
};

export const isLessThanOrEqualTo = (...bounds: number[]) => {
  const ceiling = getCeiling(...bounds);
  return (val: number = NaN) => !(val > ceiling);
};

export const isPositive = isGreaterThanOrEqualTo(0);

type FormikErrorObject<Type> = Type extends object
  ? {
      [Key in keyof Type]?: FormikErrorObject<Type[Key]> | string;
    }
  : Type extends (infer Value)[]
    ? FormikErrorObject<Value>[] | string
    : string;

export class FormikErrorManager<Type> {
  errors: Partial<FormikErrorObject<Type>>;

  constructor() {
    this.errors = {};
  }

  addError(path: string, error?: string) {
    if (error) {
      set(this.errors, path, error);
    }
  }
}

// TODO: attempt to remove this workaround after fully migrating to TSGO
/**
 * Typed translation messages cause type inferencing to recurse beyond its limit.
 * As a workaround, we do not enforce the translation type here on the implementation.
 * This allows us to infer the return type, then recast the function implementation
 * with the explicit translation types
 */
function _getValidationMessages(intl: IntlFormatters) {
  return {
    formatMessage:
      intl.formatMessage as IntlFormatters<TranslationID>["formatMessage"],
    greaterThan: (floor: number) =>
      intl.formatMessage(
        { id: "must be greater than {floor}" },
        { floor: trimExcessSignificance(floor) },
      ),
    greaterThanOrEqualTo: (floor: number) =>
      intl.formatMessage(
        { id: "must be greater than or equal to {floor}" },
        { floor: trimExcessSignificance(floor) },
      ),
    integer: () => intl.formatMessage({ id: "must be an integer" }),
    lessThan: (ceiling: number) =>
      intl.formatMessage(
        { id: "must be less than {ceiling}" },
        { ceiling: trimExcessSignificance(ceiling) },
      ),
    lessThanOrEqualTo: (ceiling: number) =>
      intl.formatMessage(
        { id: "must be less than or equal to {ceiling}" },
        { ceiling: trimExcessSignificance(ceiling) },
      ),
    notEmpty: () => intl.formatMessage({ id: "must have at least 1 value" }),
    number: () => intl.formatMessage({ id: "must be a number" }),
    positive: () => intl.formatMessage({ id: "must be positive" }),
    required: () => intl.formatMessage({ id: "required" }),
    unique: () => intl.formatMessage({ id: "must be unique" }),
    validSelection: (val: unknown) =>
      intl.formatMessage(
        { id: "must be valid selection" },
        { val: String(val) },
      ),
  };
}
export const getValidationMessages = _getValidationMessages as (
  intl: IntlFormatters<TranslationID>,
) => ReturnType<typeof _getValidationMessages>;

export type ValidationMessages = ReturnType<typeof getValidationMessages>;
